import type { ServiceLanguage } from "@albertine/i18n";
import { Lib, ServiceLanguageMap } from "albertine-shared-web";
import { useHistory, useRouteMatch } from "react-router";
import { useMember } from "@/common/hooks/useMember";
import { useSuppliers } from "@/common/hooks/useSuppliers";
import type { Request } from "@/common/types/Request";
import { getCatalogItemTranslations } from "@/common/utils/catalog.util";
import { cdnImage } from "@/common/utils/image.util";
import { hasNonNullProperty } from "@/common/utils/object.util";
import Asset from "@/components/Common/Asset";
import Button from "@/components/Common/Buttons";
import Icon from "@/components/Common/Icon";
import Label from "@/components/Common/Label";
import Tooltip from "@/components/Common/Tooltip";
import TranslationTabs from "@/components/Common/TranslationTabs";
import { useCatalogSelectionUrlParams } from "@/utils/customHook.util";
import routes from "@/utils/routes";
import { CartButton } from "../Catalog";
import { CatalogItemDetailsPrimary } from "../CatalogItemDetails/CatalogItemDetailsPrimary";
import { LanguageTab } from "../CatalogItemDetails/LanguageTab";
import { parseViewDetails } from "../CatalogItemView";
import { DistanceBadges } from "../DistanceBadges";
import TagsWithCategories from "../TagsWithCategories";
import WithGoogle from "../WithGoogle";
import "./PreviewCard.css";
import type { CatalogItemFull } from "@albertine/types/openapi/lmt";
import { getCatalogItemKeyDetail } from "@/common/CatalogItem";

export function PreviewCard({
    item,
    request,
    cartItems,
    onCartItemClick,
    mapCenter,
}: {
    item: CatalogItemFull;
    request: Request | undefined;
    cartItems: string[];
    onCartItemClick: (item: CatalogItemFull) => void;
    mapCenter: { lat: number; lng: number } | undefined;
}) {
    const matchMemberId = useRouteMatch<{
        memberId: string;
    }>("/member/:memberId?")?.params.memberId;
    const memberId = matchMemberId || request?.member;

    const { urlState, setUrlState } = useCatalogSelectionUrlParams();
    const { data: member } = useMember(memberId);
    const { data: suppliers } = useSuppliers();
    const history = useHistory();

    const de = getCatalogItemTranslations(item, "de");
    const fr = getCatalogItemTranslations(item, "fr");

    const translations: Record<ServiceLanguage, React.ReactNode> = {
        en: (
            <LanguageTab
                item={item}
                translation={getCatalogItemTranslations(item, "en")}
            />
        ),
        de: hasNonNullProperty(de) ? (
            <LanguageTab item={item} translation={de} />
        ) : undefined,
        fr: hasNonNullProperty(fr) ? (
            <LanguageTab item={item} translation={fr} />
        ) : undefined,
    };

    const { tags, images } = parseViewDetails(item, suppliers || []);
    const keyDetail = getCatalogItemKeyDetail(item);

    function goToEditMode() {
        history.push(
            routes.catalogEdit(urlState.context, item.id, request?.id, member?.id),
        );
    }

    function goToFullScreen() {
        history.push(
            routes.catalogView(urlState.context, item.id, request?.id, member?.id),
        );
    }

    function closePreview() {
        setUrlState({
            catalogId: undefined,
        });
    }

    return (
        <Lib.Flex.Column className="preview-card" gap="value16">
            <Lib.Flex.Row justifyContent="space-between" gap="value8">
                <Lib.Flex.Column>
                    <h4>{item.name}</h4>
                    <Label.Small.N50>{keyDetail}</Label.Small.N50>
                </Lib.Flex.Column>

                <Button.Ghost onClick={closePreview}>
                    <Icon.Close.Medium.N10 />
                </Button.Ghost>
            </Lib.Flex.Row>

            {images.length > 0 ? (
                <Lib.Flex.Row gap="value8" className="preview-card__images">
                    {images.map((it) => (
                        <Asset
                            key={it.id}
                            className="preview-card__image"
                            src={cdnImage(it.url, {
                                height: 100,
                                width: 100,
                            })}
                        />
                    ))}
                </Lib.Flex.Row>
            ) : null}

            <Lib.Flex.Row justifyContent="space-between" gap="value8">
                <CartButton
                    tooltipPlacement="bottom-left"
                    context={urlState?.context}
                    currentItem={item}
                    cartItems={cartItems}
                    onClick={() => onCartItemClick(item)}
                    member={member}
                />

                <Lib.Flex.Row justifyContent="end" gap="value8">
                    <Tooltip label="Edit" placement="bottom">
                        <Button iconOnly onClick={goToEditMode}>
                            <Icon.Edit.Medium.N10 />
                        </Button>
                    </Tooltip>

                    <Tooltip label="Open in full screen" placement="bottom-right">
                        <Button iconOnly onClick={goToFullScreen}>
                            <Icon.OpenInNew.Medium.N10 />
                        </Button>
                    </Tooltip>
                </Lib.Flex.Row>
            </Lib.Flex.Row>

            <DistanceBadges item={item} mapCenter={mapCenter} />

            <WithGoogle>
                {(googlePlaceService) => (
                    <CatalogItemDetailsPrimary
                        item={item}
                        googlePlaceService={googlePlaceService}
                    />
                )}
            </WithGoogle>

            <TranslationTabs
                translationOptions={[]}
                tabs={Object.entries(translations)
                    .filter(([_key, value]) => value)
                    .map(([key, value]) => ({
                        key: key as ServiceLanguage, // TODO: Remove the need for setting type
                        text: ServiceLanguageMap[key as ServiceLanguage] || key,
                        children: value,
                    }))}
            />

            <Lib.Flex.Column gap="value16">
                <h4>Tags</h4>

                <TagsWithCategories
                    offeringCategory={
                        item.offeringCategories ? item.offeringCategories[0] : undefined
                    }
                    values={tags}
                />
            </Lib.Flex.Column>
        </Lib.Flex.Column>
    );
}
